import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getOnboardingStatus,
  completeOnboarding,
  saveOnboardingProgress,
  getAppSettings,
  saveAppSettings,
  getModulesSettings,
  saveModulesSettings,
  getTenantProfile,
  saveTenantProfile,
  getSmtpSettings,
  saveSmtpSettings,
} from '../api/settings.api.js';

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');

  const [tenantName, setTenantName] = useState('');
  const [tenantSlug, setTenantSlug] = useState('');
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('587');
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpFrom, setSmtpFrom] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [currency, setCurrency] = useState('XOF');
  const [currencySymbol, setCurrencySymbol] = useState('FCFA');
  const [defaultTvaPct, setDefaultTvaPct] = useState('0');
  const [modules, setModules] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const STEP_KEYS = ['tenant_profile', 'billing_defaults', 'modules', 'smtp'];
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const normalizedSlug = useMemo(
    () => String(tenantSlug || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 64),
    [tenantSlug]
  );

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [onboarding, tenant, smtp, appSettings, modulesSettings] = await Promise.all([
          getOnboardingStatus(),
          getTenantProfile(),
          getSmtpSettings().catch(() => ({})),
          getAppSettings().catch(() => ({})),
          getModulesSettings().catch(() => ({ modules: [] })),
        ]);
        if (!alive) return;
        setStatus(onboarding || null);
        setTenantName(tenant?.name || '');
        setTenantSlug(tenant?.slug || '');
        setSmtpHost(smtp?.smtp_host || '');
        setSmtpPort(String(smtp?.smtp_port || 587));
        setSmtpUser(smtp?.smtp_user || '');
        setSmtpFrom(smtp?.smtp_from || '');
        setCurrency(String(appSettings?.currency || 'XOF'));
        setCurrencySymbol(String(appSettings?.currency_symbol || 'FCFA'));
        setDefaultTvaPct(String(appSettings?.default_tva_pct || '0'));
        setModules(Array.isArray(modulesSettings?.modules) ? modulesSettings.modules : []);
        const currentStep = String(onboarding?.current_step || '').trim();
        const idx = STEP_KEYS.indexOf(currentStep);
        if (idx >= 0) setCurrentStepIndex(idx);
      } catch (err) {
        if (!alive) return;
        setError(err?.message || 'Erreur chargement onboarding');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const isAlreadyDone = useMemo(() => !!status?.completed, [status]);
  const progressPercent = useMemo(
    () => Math.round(((currentStepIndex + 1) / STEP_KEYS.length) * 100),
    [currentStepIndex]
  );
  const currentStepKey = STEP_KEYS[currentStepIndex] || STEP_KEYS[0];

  const validateStep = (stepKey) => {
    const errs = [];

    if (stepKey === 'tenant_profile') {
      if (!tenantName || String(tenantName).trim().length < 2) {
        errs.push('Le nom du tenant doit contenir au moins 2 caractères.');
      }
      if (!normalizedSlug || normalizedSlug.length < 2) {
        errs.push('Le slug tenant est invalide (minimum 2 caractères alphanumériques).');
      }
    }

    if (stepKey === 'billing_defaults') {
      const tva = Number(defaultTvaPct);
      if (!Number.isFinite(tva) || tva < 0 || tva > 100) {
        errs.push('La TVA par défaut doit être un nombre entre 0 et 100.');
      }
      if (!currency || String(currency).trim().length < 2) {
        errs.push('La devise est requise.');
      }
    }

    if (stepKey === 'smtp') {
      const smtpAny = !!(smtpHost || smtpUser || smtpFrom || smtpPassword);
      if (smtpAny) {
        const p = Number(smtpPort || 0);
        if (!smtpHost || !smtpUser || !smtpFrom) {
          errs.push('Pour activer SMTP, renseignez host, user et from.');
        }
        if (!Number.isInteger(p) || p < 1 || p > 65535) {
          errs.push('Le port SMTP doit être un entier entre 1 et 65535.');
        }
        if (smtpFrom && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(smtpFrom))) {
          errs.push('L’adresse email "from" SMTP est invalide.');
        }
      }
    }

    return errs;
  };

  const persistProgress = async (stepIdx) => {
    const boundedIdx = Math.max(0, Math.min(stepIdx, STEP_KEYS.length - 1));
    const step = STEP_KEYS[boundedIdx];
    const pct = Math.round(((boundedIdx + 1) / STEP_KEYS.length) * 100);
    await saveOnboardingProgress(step, pct);
  };

  const onNext = async () => {
    setValidationErrors([]);
    const errs = validateStep(currentStepKey);
    if (errs.length > 0) {
      setValidationErrors(errs);
      return;
    }
    const next = Math.min(currentStepIndex + 1, STEP_KEYS.length - 1);
    setCurrentStepIndex(next);
    try {
      await persistProgress(next);
    } catch (_) {}
  };

  const onBack = async () => {
    setValidationErrors([]);
    const prev = Math.max(currentStepIndex - 1, 0);
    setCurrentStepIndex(prev);
    try {
      await persistProgress(prev);
    } catch (_) {}
  };

  const onFinish = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setValidationErrors([]);
    const smtpAny = !!(smtpHost || smtpUser || smtpFrom || smtpPassword);
    const errs = [
      ...validateStep('tenant_profile'),
      ...validateStep('billing_defaults'),
      ...validateStep('smtp'),
    ];
    if (errs.length > 0) {
      setValidationErrors(errs);
      setSaving(false);
      return;
    }

    try {
      await saveTenantProfile({ name: tenantName, slug: normalizedSlug });
      await saveAppSettings({
        currency,
        currency_symbol: currencySymbol,
        default_tva_pct: defaultTvaPct,
      });
      if (smtpAny && smtpHost && smtpUser && smtpFrom) {
        await saveSmtpSettings({
          smtp_host: smtpHost,
          smtp_port: Number(smtpPort || 587),
          smtp_user: smtpUser,
          smtp_from: smtpFrom,
          ...(smtpPassword ? { smtp_pass: smtpPassword } : {}),
          smtp_secure: Number(smtpPort || 587) === 465,
          smtp_enabled: true,
        });
      }
      await saveModulesSettings(modules.map((m) => ({
        key: m.key,
        enabled: m.enabled !== false,
        config: m.config || {},
      })));
      await completeOnboarding([
        'tenant_profile',
        'billing_defaults',
        'modules',
        ...(smtpHost ? ['smtp'] : []),
      ]);
      try {
        await saveOnboardingProgress('done', 100);
      } catch (_) {}
      navigate('/orders', { replace: true });
    } catch (err) {
      setError(err?.message || 'Erreur finalisation onboarding');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-slate-700">Chargement onboarding...</div>;
  }

  if (isAlreadyDone) {
    return (
      <div className="p-6">
        <div className="max-w-xl rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-800">
          Onboarding deja termine.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl">
      <h1 className="text-2xl font-semibold text-slate-900 mb-2">Onboarding</h1>
      <p className="text-slate-600 mb-6">Finalisez votre configuration initiale.</p>
      <div className="mb-6">
        <div className="flex items-center justify-between text-xs text-slate-500 mb-2">
          <span>Etape {currentStepIndex + 1} / {STEP_KEYS.length}</span>
          <span>{progressPercent}%</span>
        </div>
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
          <div className="h-full bg-slate-900" style={{ width: `${progressPercent}%` }} />
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-rose-700">
          {error}
        </div>
      )}
      {validationErrors.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-800">
          <ul className="list-disc ml-5 space-y-1">
            {validationErrors.map((x) => <li key={x}>{x}</li>)}
          </ul>
        </div>
      )}

      <form onSubmit={onFinish} className="space-y-6">
        {currentStepKey === 'tenant_profile' && (
        <section className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="font-medium text-slate-900 mb-3">Profil tenant</h2>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-sm text-slate-700">
              Nom
              <input
                value={tenantName}
                onChange={(e) => setTenantName(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
                required
              />
            </label>
            <label className="text-sm text-slate-700">
              Slug
              <input
                value={tenantSlug}
                onChange={(e) => setTenantSlug(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
                required
              />
              <div className="mt-1 text-xs text-slate-500">Slug proposé: <code>{normalizedSlug || '-'}</code></div>
            </label>
          </div>
        </section>
        )}

        {currentStepKey === 'billing_defaults' && (
        <section className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="font-medium text-slate-900 mb-3">Devise et taxe</h2>
          <div className="grid gap-3 md:grid-cols-3">
            <label className="text-sm text-slate-700">
              Devise
              <input value={currency} onChange={(e) => setCurrency(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
            </label>
            <label className="text-sm text-slate-700">
              Symbole
              <input value={currencySymbol} onChange={(e) => setCurrencySymbol(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
            </label>
            <label className="text-sm text-slate-700">
              TVA par defaut (%)
              <input value={defaultTvaPct} onChange={(e) => setDefaultTvaPct(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
            </label>
          </div>
        </section>
        )}

        {currentStepKey === 'modules' && (
        <section className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="font-medium text-slate-900 mb-3">Modules actifs</h2>
          <div className="grid gap-2 md:grid-cols-2">
            {modules.map((m) => (
              <label key={m.key} className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={m.enabled !== false}
                  onChange={(e) => {
                    setModules((prev) => prev.map((x) => (
                      x.key === m.key ? { ...x, enabled: e.target.checked } : x
                    )));
                  }}
                />
                <span>{m.label || m.key}</span>
              </label>
            ))}
          </div>
        </section>
        )}

        {currentStepKey === 'smtp' && (
        <section className="rounded-xl border border-slate-200 bg-white p-4">
          <h2 className="font-medium text-slate-900 mb-3">SMTP (optionnel)</h2>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-sm text-slate-700">
              Host
              <input value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
            </label>
            <label className="text-sm text-slate-700">
              Port
              <input value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
            </label>
            <label className="text-sm text-slate-700">
              User
              <input value={smtpUser} onChange={(e) => setSmtpUser(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
            </label>
            <label className="text-sm text-slate-700">
              From
              <input value={smtpFrom} onChange={(e) => setSmtpFrom(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
            </label>
            <label className="text-sm text-slate-700 md:col-span-2">
              Password (laisser vide pour conserver)
              <input type="password" value={smtpPassword} onChange={(e) => setSmtpPassword(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
            </label>
          </div>
        </section>
        )}

        <div className="flex items-center gap-3">
          {currentStepIndex > 0 && (
            <button
              type="button"
              onClick={onBack}
              className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 hover:bg-slate-50"
            >
              Précédent
            </button>
          )}
          {currentStepIndex < STEP_KEYS.length - 1 ? (
            <button
              type="button"
              onClick={onNext}
              className="rounded-lg bg-slate-900 text-white px-4 py-2 hover:bg-slate-800"
            >
              Suivant
            </button>
          ) : (
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-slate-900 text-white px-4 py-2 hover:bg-slate-800 disabled:opacity-60"
            >
              {saving ? 'Finalisation...' : 'Terminer onboarding'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
